import { forwardRef } from '@chakra-ui/system';
import { AnimatePresence, motion } from 'framer-motion';
import type { ForwardRefExoticComponent, RefAttributes } from 'react';

import {
  Center,
  CircularProgress,
  type CircularProgressProps,
  type IconProps,
} from '@frontend/components';
import {
  UploadDiskIcon,
  PaperlessCubeIcon,
  HourglassIcon,
} from '@frontend/components/src/components/mediaAndIcons/icons/iconSet';

export type ProgressStep = {
  key: string;
  label: string;
  isIndeterminate: boolean;
  icon: ForwardRefExoticComponent<
    IconProps & RefAttributes<SVGSVGElement>
  > | null;
  color: string;
};

export const PROGRESS_STEPS = {
  unknown: {
    key: 'unknown',
    label: 'Uploading',
    isIndeterminate: true,
    icon: null,
    color: '#767676',
  },
  uploading: {
    key: 'uploading',
    label: 'Uploading',
    isIndeterminate: false,
    icon: UploadDiskIcon,
    color: '#767676',
  },
  waiting: {
    key: 'waiting',
    label: 'Waiting to process',
    isIndeterminate: true,
    icon: HourglassIcon,
    color: '#767676',
  },
  processing: {
    key: 'processing',
    label: 'Processing',
    isIndeterminate: true,
    icon: PaperlessCubeIcon,
    color: '#ff771a',
  },
};

// CircularProgress does not support Chakra sizing, so this acts as a 'polyfill'
export type PartFileCircularProgressSizes = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type PartFileCircularProgressProps = {
  step: keyof typeof PROGRESS_STEPS;
  value?: number;
  thickness?: string; // string value for px or rem
  size?: number | PartFileCircularProgressSizes;
};

export const PartFileCircularProgress = forwardRef<
  PartFileCircularProgressProps & CircularProgressProps,
  'div'
>(({ step, value, size, thickness, ...progressProps }, ref) => {
  const Icon = PROGRESS_STEPS[step].icon;
  const color = PROGRESS_STEPS[step].color;

  let cssSize = typeof size === 'number' ? size : 3;

  if (typeof size === 'string') {
    switch (size) {
      case 'xs':
        cssSize = 1.5;
        break;
      case 'sm':
        cssSize = 2;
        break;
      case 'md':
        cssSize = 3;
        break;
      case 'lg':
        cssSize = 4;
        break;
      case 'xl':
        cssSize = 6;
        break;
      default:
        break;
    }
  }

  const isIndeterminate =
    PROGRESS_STEPS[step].isIndeterminate ||
    (!PROGRESS_STEPS[step].isIndeterminate && value === undefined);

  return (
    <CircularProgress
      ref={ref}
      color={color}
      gridTemplateAreas="'main'"
      placeItems="stretch"
      display="grid"
      trackColor="transparent"
      thickness={thickness ?? '7px'}
      size={cssSize}
      sx={{
        '> svg': {
          gridArea: 'main',
        },
      }}
      {...progressProps}
      {...(isIndeterminate ? { isIndeterminate } : { value })}
    >
      <AnimatePresence initial={false}>
        <Center
          as={motion.div}
          initial={{ opacity: 0.5, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          gridArea="main"
          key={PROGRESS_STEPS[step].key}
        >
          {PROGRESS_STEPS[step].icon && (
            <Icon color={color} h={cssSize / 2} w={cssSize / 2} />
          )}
        </Center>
      </AnimatePresence>
    </CircularProgress>
  );
});
